import { useMemo } from "react";
import useCapabilities from "@/hooks/useCapabilities";
import useUpcomingTasks from "./useUpcomingTasks";
import useDashboardEvents from "./useDashboardEvents";
import usePendingChangeRequests from "./usePendingChangeRequests";
import { HQ_WIDE_ROLES } from "../constants";
import { getDashboardSubtitle, getDashboardSubtitleDomain } from "../_utils/dashboardSubtitle";

export default function useDashboardSubtitle() {
  const { hasRole, loaded: capabilitiesLoaded } = useCapabilities();
  const { loading: tasksLoading, totalOpenTaskCount, canViewTasks } = useUpcomingTasks();
  const { canViewEvents, hasSectionScope, sectionScopeReady, scheduleEventCount, scheduleLoading } =
    useDashboardEvents();
  const {
    sectionId: changeRequestsSectionId,
    totalPendingCount,
    canManageChangeRequests,
    permissionProbeLoading,
    loading: changeRequestsLoading
  } = usePendingChangeRequests();

  const isHqStaff = HQ_WIDE_ROLES.some((role) => hasRole(role));
  const domain = getDashboardSubtitleDomain(isHqStaff);
  const taskCount = canViewTasks ? totalOpenTaskCount : null;
  const eventCount = canViewEvents && hasSectionScope ? scheduleEventCount : null;
  const changeRequestCount = canManageChangeRequests ? totalPendingCount : null;

  const shouldWaitForChangeRequests =
    !!changeRequestsSectionId &&
    (canManageChangeRequests || (permissionProbeLoading && !canViewTasks)) &&
    changeRequestsLoading;

  const loading =
    !capabilitiesLoaded ||
    (canViewTasks && tasksLoading) ||
    shouldWaitForChangeRequests ||
    (canViewEvents && (!sectionScopeReady || (hasSectionScope && scheduleLoading)));

  const subtitle = useMemo(() => {
    if (loading) {
      return null;
    }

    return getDashboardSubtitle({
      domain,
      taskCount,
      eventCount,
      changeRequestCount
    });
  }, [changeRequestCount, domain, eventCount, loading, taskCount]);

  return {
    subtitle,
    loading
  };
}
